import { assign, sendParent, setup } from 'xstate';
import { findClientsActor, registerClientActor } from './actors';
import type { ClientEvent } from './events';
import type { RegisterClientInput } from '@/lib/gql/__generated__/graphql';

type ClientRow = { clientId: string; clientName?: string | null; fiduciaryName?: string | null };
type ClientContext = { clients: ClientRow[]; error: unknown; pendingRegistration: RegisterClientInput | null };

const clientSetup = setup({
  types: {
    context: {} as ClientContext,
    events: {} as ClientEvent,
  },
  actors: { findClientsActor, registerClientActor },
});

export const clientMachine = clientSetup.createMachine({
  id: 'client',
  initial: 'idle',
  context: { clients: [], error: null, pendingRegistration: null },
  states: {
    idle: {
      on: {
        'client.load-clients': 'loading',
        'client.register': {
          target: 'registering',
          actions: assign({
            pendingRegistration: ({ event }) => ({
              clientId: crypto.randomUUID(),
              clientName: event.clientName,
              fiduciaryName: event.fiduciaryName,
              beneficiaries: event.beneficiaries.map((beneficiaryName) => ({ beneficiaryName })),
            }),
          }),
        },
      },
    },
    loading: {
      invoke: {
        src: 'findClientsActor',
        onDone: { target: 'ready', actions: assign({ clients: ({ event }) => event.output, error: null }) },
        onError: { target: 'error', actions: assign({ error: ({ event }) => event.error }) },
      },
      on: { 'client.load-clients': 'loading' },
    },
    ready: {
      on: {
        'client.load-clients': 'loading',
        'client.register': {
          target: 'registering',
          actions: assign({
            pendingRegistration: ({ event }) => ({
              clientId: crypto.randomUUID(),
              clientName: event.clientName,
              fiduciaryName: event.fiduciaryName,
              beneficiaries: event.beneficiaries.map((beneficiaryName) => ({ beneficiaryName })),
            }),
          }),
        },
      },
    },
    error: {
      on: {
        'client.load-clients': 'loading',
        'client.register': {
          target: 'registering',
          actions: assign({
            pendingRegistration: ({ event }) => ({
              clientId: crypto.randomUUID(),
              clientName: event.clientName,
              fiduciaryName: event.fiduciaryName,
              beneficiaries: event.beneficiaries.map((beneficiaryName) => ({ beneficiaryName })),
            }),
          }),
        },
      },
    },
    registering: {
      invoke: {
        src: 'registerClientActor',
        input: ({ context }) =>
          context.pendingRegistration ?? {
            clientId: crypto.randomUUID(),
            clientName: '',
            fiduciaryName: '',
            beneficiaries: [],
          },
        onDone: {
          target: 'loading',
          actions: [
            sendParent(({ context }) => ({
              type: 'client.registered',
              clientId: context.pendingRegistration?.clientId ?? '',
            })),
            sendParent({ type: 'ui.toast.push', toast: { title: 'Client registered', tone: 'success' } }),
          ],
        },
        onError: {
          target: 'error',
          actions: [
            assign({ error: ({ event }) => event.error }),
            sendParent({
              type: 'ui.toast.push',
              toast: {
                title: 'Client registration failed',
                description: 'Review the form and try again.',
                tone: 'error',
              },
            }),
          ],
        },
      },
    },
  },
});
